/**
 * <ConceptMap> — /study/concept-map
 *
 * Recall/Connect Track B — the concept-map view. Renders every study
 * deck as a node on a circle; every pair of decks that share a Bridge
 * card is connected by an edge whose thickness matches how many
 * Bridge cards make that link. Node colour = the student's personal
 * mastery on that deck. Click a node to inspect it (mastery, review
 * count, link to open the deck).
 *
 * Rendered entirely in SVG — no third-party graph library. Circular
 * layout keeps things predictable up to ~30 nodes; if the class grows
 * beyond that we'll swap in a force-directed layout.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { STUDY_THEME, paperBackground } from '../components/study/studyTheme';
import { getConceptMap } from '../lib/studyApi';

const RADIUS_OUTER = 340;
const RADIUS_NODE  = 24;

function masteryColor(m) {
  if (m >= 75) return '#16A34A';
  if (m >= 50) return STUDY_THEME.goldDark;
  if (m > 0)   return '#F97316';
  return STUDY_THEME.muted;
}

function nodePositions(nodes) {
  const n = nodes.length;
  if (n === 0) return {};
  const cx = 0, cy = 0;
  const step = (Math.PI * 2) / n;
  const pos = {};
  nodes.forEach((node, i) => {
    // Start at top (-π/2) and go clockwise.
    const angle = -Math.PI / 2 + step * i;
    pos[node.id] = {
      x: cx + RADIUS_OUTER * Math.cos(angle),
      y: cy + RADIUS_OUTER * Math.sin(angle),
      angle,
    };
  });
  return pos;
}

export default function ConceptMap() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr]  = useState('');
  const [selected, setSelected] = useState(null);
  const [hoverEdge, setHoverEdge] = useState(null);

  useEffect(() => {
    getConceptMap().then(setData).catch((e) =>
      setErr(e?.response?.data?.detail || e.message || 'Load failed'));
  }, []);

  const positions = useMemo(
    () => (data ? nodePositions(data.nodes) : {}),
    [data],
  );

  const maxWeight = useMemo(() => {
    if (!data || !data.edges.length) return 1;
    return Math.max(...data.edges.map((e) => e.weight));
  }, [data]);

  const selectedNode = data && selected
    ? data.nodes.find((n) => n.id === selected)
    : null;

  const selectedEdges = data && selected
    ? data.edges.filter((e) => e.source === selected || e.target === selected)
    : [];

  return (
    <div style={paperBackground()} data-testid="concept-map-page">
      <div style={{ maxWidth: 1240, margin: '0 auto', padding: '32px 24px' }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', marginBottom: 24, gap: 20,
        }}>
          <div>
            <div style={{
              fontSize: 11, letterSpacing: 1.6, textTransform: 'uppercase',
              fontWeight: 700, color: STUDY_THEME.goldDeep,
            }}>Institute of Analytics · Recall &amp; Connect</div>
            <h1 style={{
              fontFamily: '"Playfair Display", serif', fontSize: 40,
              margin: '2px 0 4px', color: STUDY_THEME.ink,
            }}>🕸 Concept Map</h1>
            <p style={{ color: STUDY_THEME.inkSoft, margin: 0, fontSize: 15 }}>
              Every deck is a node. Every line is a bridge card linking two
              concepts. Colour shows your personal mastery on that node.
              Click any node to inspect it.
            </p>
          </div>
          <button
            onClick={() => navigate('/study')}
            data-testid="concept-map-back"
            style={{
              padding: '8px 14px', fontSize: 13, fontWeight: 600,
              background: 'white', color: STUDY_THEME.inkSoft,
              border: `1px solid ${STUDY_THEME.line}`, borderRadius: 6,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >← Deck Hub</button>
        </div>

        {err && (
          <div data-testid="concept-map-error" style={{
            padding: 14, borderRadius: 10, background: '#FEE2E2',
            color: '#991B1B', marginBottom: 16,
          }}>{err}</div>
        )}

        {!data ? (
          <div style={{ padding: 40, textAlign: 'center', color: STUDY_THEME.muted }}>
            Building the graph…
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20 }}>
            {/* Graph */}
            <div style={{
              background: 'white', borderRadius: 14, padding: 20,
              boxShadow: STUDY_THEME.shadow, border: `1px solid ${STUDY_THEME.line}`,
              overflow: 'hidden',
            }}>
              <div style={{
                fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase',
                fontWeight: 700, color: STUDY_THEME.goldDeep, marginBottom: 6,
              }}>Cross-concept graph — {data.nodes.length} decks · {data.edges.length} bridges</div>

              <svg
                viewBox={`${-RADIUS_OUTER - 60} ${-RADIUS_OUTER - 60} ${(RADIUS_OUTER + 60) * 2} ${(RADIUS_OUTER + 60) * 2}`}
                style={{ width: '100%', height: 720, display: 'block' }}
                data-testid="concept-map-svg"
              >
                {/* Edges */}
                {data.edges.map((e, i) => {
                  const a = positions[e.source];
                  const b = positions[e.target];
                  if (!a || !b) return null;
                  const isHover = hoverEdge === i;
                  const isTouched = selected && (e.source === selected || e.target === selected);
                  const thickness = 0.8 + (e.weight / maxWeight) * 3.6;
                  const stroke = isHover || isTouched
                    ? STUDY_THEME.goldDeep
                    : 'rgba(15, 23, 42, 0.28)';
                  return (
                    <line
                      key={`${e.source}-${e.target}`}
                      x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                      stroke={stroke}
                      strokeWidth={thickness}
                      strokeLinecap="round"
                      onMouseEnter={() => setHoverEdge(i)}
                      onMouseLeave={() => setHoverEdge(null)}
                      style={{ cursor: 'pointer', transition: 'stroke 0.15s' }}
                      data-testid={`edge-${e.source}-${e.target}`}
                    />
                  );
                })}

                {/* Nodes */}
                {data.nodes.map((n) => {
                  const pos = positions[n.id];
                  if (!pos) return null;
                  const isSelected = selected === n.id;
                  const color = masteryColor(n.mastery);
                  // Text position — push labels outward from the ring.
                  const labelX = pos.x + Math.cos(pos.angle) * (RADIUS_NODE + 20);
                  const labelY = pos.y + Math.sin(pos.angle) * (RADIUS_NODE + 20);
                  const anchor = Math.cos(pos.angle) > 0.2 ? 'start'
                              : Math.cos(pos.angle) < -0.2 ? 'end'
                              : 'middle';
                  return (
                    <g key={n.id}
                       onClick={() => setSelected(n.id)}
                       style={{ cursor: 'pointer' }}
                       data-testid={`node-${n.id}`}>
                      <circle
                        cx={pos.x} cy={pos.y}
                        r={RADIUS_NODE + (isSelected ? 4 : 0)}
                        fill={color}
                        stroke={isSelected ? STUDY_THEME.ink : 'white'}
                        strokeWidth={isSelected ? 3 : 2}
                        opacity={n.reviewed > 0 ? 1 : 0.55}
                      />
                      <text
                        x={pos.x} y={pos.y + 4}
                        fill="white" fontWeight="700" fontSize="12"
                        textAnchor="middle"
                        style={{ pointerEvents: 'none' }}
                      >{n.order && n.order < 100 ? n.order
                        : n.week != null ? `W${n.week}`
                        : '•'}</text>
                      <text
                        x={labelX} y={labelY + 4}
                        fill={STUDY_THEME.ink}
                        fontSize="10.5" fontWeight={isSelected ? '700' : '500'}
                        textAnchor={anchor}
                        style={{ pointerEvents: 'none', maxWidth: 140 }}
                      >
                        {(n.label || n.id).length > 28
                          ? `${(n.label || n.id).slice(0, 26)}…`
                          : (n.label || n.id)}
                      </text>
                    </g>
                  );
                })}
              </svg>

              <div style={{
                display: 'flex', gap: 20, justifyContent: 'center',
                marginTop: 4, flexWrap: 'wrap',
                fontSize: 11, color: STUDY_THEME.muted,
              }}>
                <span><span style={{
                  display: 'inline-block', width: 10, height: 10, borderRadius: 5,
                  background: '#16A34A', marginRight: 5, verticalAlign: 'middle',
                }} />≥75% mastered</span>
                <span><span style={{
                  display: 'inline-block', width: 10, height: 10, borderRadius: 5,
                  background: STUDY_THEME.goldDark, marginRight: 5, verticalAlign: 'middle',
                }} />50–74%</span>
                <span><span style={{
                  display: 'inline-block', width: 10, height: 10, borderRadius: 5,
                  background: '#F97316', marginRight: 5, verticalAlign: 'middle',
                }} />1–49%</span>
                <span><span style={{
                  display: 'inline-block', width: 10, height: 10, borderRadius: 5,
                  background: STUDY_THEME.muted, marginRight: 5, verticalAlign: 'middle',
                }} />not started</span>
              </div>
            </div>

            {/* Detail rail */}
            <div style={{
              background: 'white', borderRadius: 14, padding: 20,
              boxShadow: STUDY_THEME.shadow, border: `1px solid ${STUDY_THEME.line}`,
              height: 'fit-content', position: 'sticky', top: 24,
            }} data-testid="concept-map-detail">
              {!selectedNode ? (
                <>
                  <div style={{
                    fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase',
                    fontWeight: 700, color: STUDY_THEME.goldDeep, marginBottom: 8,
                  }}>Node inspector</div>
                  <div style={{
                    fontFamily: '"Playfair Display", serif', fontSize: 20,
                    color: STUDY_THEME.ink, marginBottom: 6,
                  }}>Click a node</div>
                  <p style={{ fontSize: 13, color: STUDY_THEME.inkSoft, margin: 0 }}>
                    Each node is a deck (a topic in your course). Click one
                    to see your mastery, the bridge cards it participates in,
                    and jump straight to a review session.
                  </p>
                </>
              ) : (
                <>
                  <div style={{
                    fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase',
                    fontWeight: 700, color: STUDY_THEME.goldDeep, marginBottom: 6,
                  }}>{selectedNode.mastery > 0
                    ? `Mastery ${selectedNode.mastery.toFixed(0)}%`
                    : 'Not reviewed yet'}</div>
                  <div style={{
                    fontFamily: '"Playfair Display", serif', fontSize: 22,
                    color: STUDY_THEME.ink, marginBottom: 8, lineHeight: 1.3,
                  }} data-testid="detail-title">{selectedNode.label}</div>
                  <div style={{
                    fontSize: 12, color: STUDY_THEME.muted, marginBottom: 14,
                  }}>
                    {selectedNode.cards} cards · {selectedNode.reviewed} reviewed by you
                  </div>

                  <button
                    onClick={() => navigate(`/study/session?deck=${selectedNode.id}`)}
                    data-testid="detail-review-btn"
                    style={{
                      width: '100%', padding: '10px 16px', fontSize: 14,
                      fontWeight: 700, background: STUDY_THEME.gold,
                      color: STUDY_THEME.ink,
                      border: `1px solid ${STUDY_THEME.goldDark}`, borderRadius: 8,
                      cursor: 'pointer', fontFamily: 'inherit', marginBottom: 16,
                    }}
                  >Review this deck →</button>

                  <div style={{
                    fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase',
                    fontWeight: 700, color: STUDY_THEME.goldDeep, marginBottom: 8,
                  }}>Bridges from here — {selectedEdges.length}</div>
                  {selectedEdges.length === 0 ? (
                    <div style={{ fontSize: 12, color: STUDY_THEME.muted }}>
                      This deck has no bridge cards yet.
                    </div>
                  ) : selectedEdges.map((e, i) => {
                    const other_id = e.source === selectedNode.id ? e.target : e.source;
                    const other = data.nodes.find((n) => n.id === other_id);
                    return (
                      <div key={i} data-testid={`bridge-${i}`}
                           style={{
                             padding: '8px 10px',
                             borderBottom: `1px solid ${STUDY_THEME.line}`,
                           }}>
                        <button
                          onClick={() => setSelected(other_id)}
                          style={{
                            background: 'none', border: 'none', padding: 0,
                            fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                            color: STUDY_THEME.goldDeep, cursor: 'pointer',
                            textAlign: 'left', textDecoration: 'underline',
                          }}
                        >→ {other?.label || other_id}</button>
                        <div style={{
                          fontSize: 11, color: STUDY_THEME.muted, marginTop: 2,
                        }}>{e.weight} bridge card{e.weight === 1 ? '' : 's'}</div>
                        {e.example_fronts?.[0] && (
                          <div style={{
                            fontSize: 11, color: STUDY_THEME.inkSoft,
                            marginTop: 4, fontStyle: 'italic',
                          }}>
                            &ldquo;{e.example_fronts[0].front}&rdquo;
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
